import React, { useState, useEffect, useCallback } from 'react';
import { cashBanksApi } from '../../utils/cashBanksApi';
import { entitiesApi } from '../../utils/adminApi';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './CashBanks.css';

const today = () => new Date().toISOString().slice(0, 10);

const fmt = (val) =>
  val != null
    ? Number(val).toLocaleString('en-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

export default function DailyClosingPage() {
  const [entities, setEntities] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [closings, setClosings] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [closeDate, setCloseDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState({}); // accountId → preview data
  const [previewLoading, setPreviewLoading] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const loadEntities = useCallback(async () => {
    try {
      const data = await entitiesApi.list();
      setEntities(data || []);
    } catch {
      // entities are optional — don't block
    }
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = selectedEntity ? { entityId: selectedEntity } : {};
      const data = await cashBanksApi.listAccounts(params);
      setAccounts(data || []);
    } catch (e) {
      setError(e.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [selectedEntity]);

  const loadClosings = useCallback(async () => {
    try {
      const data = await cashBanksApi.listClosings({
        closeDate,
        ...(selectedEntity ? { entityId: selectedEntity } : {}),
      });
      const map = {};
      (data || []).forEach((c) => {
        if (!map[c.accountId]) map[c.accountId] = {};
        map[c.accountId][c.step] = c;
      });
      setClosings(map);
    } catch {
      // non-blocking
    }
  }, [closeDate, selectedEntity]);

  useEffect(() => { loadEntities(); }, [loadEntities]);
  useEffect(() => { loadAccounts(); loadClosings(); }, [loadAccounts, loadClosings]);

  const handlePreview = async (accountId, step) => {
    const key = `${accountId}-${step}`;
    setPreviewLoading((p) => ({ ...p, [key]: true }));
    try {
      const data = await cashBanksApi.previewClosing({
        accountId,
        closeDate,
        step,
        ...(selectedEntity ? { entityId: selectedEntity } : {}),
      });
      setPreviews((p) => ({ ...p, [key]: data }));
    } catch (e) {
      setError(e.message);
    } finally {
      setPreviewLoading((p) => ({ ...p, [key]: false }));
    }
  };

  const handleClose = async (accountId, step) => {
    const key = `${accountId}-${step}`;
    setActionLoading((a) => ({ ...a, [key]: true }));
    setError('');
    try {
      await cashBanksApi.createClosing({
        accountId,
        closeDate,
        step,
        ...(selectedEntity ? { entityId: selectedEntity } : {}),
      });
      showToast(`Step ${step} closing saved successfully`);
      setPreviews((p) => { const n = { ...p }; delete n[key]; return n; });
      loadClosings();
    } catch (e) {
      setError(e.message);
    } finally {
      setActionLoading((a) => ({ ...a, [key]: false }));
    }
  };

  const getClosingStatus = (accountId) => {
    const c = closings[accountId];
    if (!c) return { step1: false, step2: false };
    return { step1: !!c[1], step2: !!c[2] };
  };

  return (
    <div className="cb-page">
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, background: '#065f46', color: '#fff', padding: '10px 18px', borderRadius: 8, zIndex: 2000, fontWeight: 600 }}>
          <i className="fas fa-check-circle" style={{ marginRight: 8 }} />{toast}
        </div>
      )}

      <div className="cb-header">
        <div className="cb-header-text">
          <h1 className="cb-title"><i className="fas fa-lock" style={{ marginRight: 8, color: 'var(--color-primary)' }} />Daily Closing</h1>
          <p className="cb-subtitle">Run 1st and 2nd daily closing per account and sub-entity</p>
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>
          <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />{error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b' }}>×</button>
        </div>
      )}

      <div className="cb-toolbar">
        <label className="cb-label" style={{ margin: 0 }}>Date:</label>
        <input
          type="date"
          value={closeDate}
          onChange={(e) => setCloseDate(e.target.value)}
          max={today()}
        />
        <label className="cb-label" style={{ margin: 0 }}>Sub-Entity:</label>
        <select value={selectedEntity} onChange={(e) => setSelectedEntity(e.target.value)}>
          <option value="">All Entities</option>
          {entities.map((e) => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <LoadingSpinner variant="default" />
      ) : accounts.length === 0 ? (
        <div className="cb-card">
          <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
            <i className="fas fa-piggy-bank" style={{ fontSize: 32, marginBottom: 12, display: 'block' }} />
            No cash or bank accounts found. Mark accounts as <strong>isBank</strong> or <strong>isCashOnHand</strong> in the Chart of Accounts.
          </div>
        </div>
      ) : (
        <div className="cb-wizard">
          {accounts.map((acc) => {
            const { step1, step2 } = getClosingStatus(acc.id);
            const prevKey1 = `${acc.id}-1`;
            const prevKey2 = `${acc.id}-2`;
            const prev1 = previews[prevKey1];
            const prev2 = previews[prevKey2];

            return (
              <div key={acc.id} className={`cb-account-card ${step2 ? 'is-closed' : ''}`}>
                <div>
                  <div className="cb-account-card__name">{acc.name}</div>
                  <div className="cb-account-card__code">{acc.code}</div>
                  {acc.entity && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{acc.entity.name}</div>}
                </div>

                <div style={{ display: 'flex', gap: 6 }}>
                  <span className={`cb-badge cb-badge--${acc.isBank ? 'bank' : 'cash'}`}>
                    <i className={`fas fa-${acc.isBank ? 'university' : 'wallet'}`} />
                    {acc.isBank ? 'Bank' : 'Cash'}
                  </span>
                  <span className={`cb-badge ${step2 ? 'cb-badge--closed' : step1 ? 'cb-badge--issued' : 'cb-badge--open'}`}>
                    {step2 ? 'Fully Closed' : step1 ? 'Step 1 Done' : 'Open'}
                  </span>
                </div>

                {prev1 && (
                  <div style={{ background: 'var(--surface-2)', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
                    <strong>Step 1 preview:</strong> Balance = <span className="cb-account-card__balance">{fmt(prev1.closingBalance)} {acc.currency?.code}</span>
                  </div>
                )}
                {prev2 && (
                  <div style={{ background: 'var(--surface-2)', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
                    <strong>Step 2 preview:</strong> Balance = <span className="cb-account-card__balance">{fmt(prev2.closingBalance)} {acc.currency?.code}</span>
                  </div>
                )}

                <div className="cb-account-card__actions">
                  {!step1 && (
                    <>
                      <button className="cb-btn cb-btn--secondary cb-btn--sm" onClick={() => handlePreview(acc.id, 1)} disabled={previewLoading[prevKey1]}>
                        {previewLoading[prevKey1] ? <LoadingSpinner variant="button" /> : <><i className="fas fa-eye" /> Preview Step 1</>}
                      </button>
                      {prev1 && (
                        <button className="cb-btn cb-btn--primary cb-btn--sm" onClick={() => handleClose(acc.id, 1)} disabled={actionLoading[prevKey1]}>
                          {actionLoading[prevKey1] ? <LoadingSpinner variant="button" /> : <><i className="fas fa-lock" /> Close Step 1</>}
                        </button>
                      )}
                    </>
                  )}
                  {step1 && !step2 && (
                    <>
                      <button className="cb-btn cb-btn--secondary cb-btn--sm" onClick={() => handlePreview(acc.id, 2)} disabled={previewLoading[prevKey2]}>
                        {previewLoading[prevKey2] ? <LoadingSpinner variant="button" /> : <><i className="fas fa-eye" /> Preview Step 2</>}
                      </button>
                      {prev2 && (
                        <button className="cb-btn cb-btn--danger cb-btn--sm" onClick={() => handleClose(acc.id, 2)} disabled={actionLoading[prevKey2]}>
                          {actionLoading[prevKey2] ? <LoadingSpinner variant="button" /> : <><i className="fas fa-lock" /> Final Close (Step 2)</>}
                        </button>
                      )}
                    </>
                  )}
                  {step2 && (
                    <span style={{ fontSize: 13, color: '#065f46' }}>
                      <i className="fas fa-check-double" style={{ marginRight: 4 }} />Fully closed for {closeDate}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
